import React, { useEffect, useState } from 'react';
import { Plus, Download, Trash2, Pencil, X, FileText, ChevronDown, History } from 'lucide-react';
import { getInventoryTypeLabel, formatCurrency, formatDate } from '@/lib/utils';
import { exportAPI, inventoriesAPI, downloadAuthFile, sitesAPI } from '@/lib/api';
import PrintButton from '@/components/PrintButton';
import { resolveInventoryUnits } from '@/lib/inventory';

// Business rule: non variable-weight supplier products are counted with 0.1
// precision (no more than one decimal). Variable-weight items keep free-form
// decimals so users can weigh e.g. cheese slices.
const qtyStepFor = (sp) => (sp && sp.variable_weight ? 'any' : '0.1');
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function InventoryDialogs({
    // Dialog states
    inventoryDialog, setInventoryDialog,
    inventoryDetailDialog, setInventoryDetailDialog,
    receptionDialog, setReceptionDialog,
    deleteDialog, setDeleteDialog,
    deleteInventoryDialog, setDeleteInventoryDialog,
    deleteGroupDialog, setDeleteGroupDialog,
    deleteGroupConfirmDialog, setDeleteGroupConfirmDialog,
    inventoryFormPdfDialog, setInventoryFormPdfDialog,
    // Form data
    inventoryForm, setInventoryForm,
    inventoryItem, setInventoryItem,
    detailAddItem, setDetailAddItem,
    receptionForm, setReceptionForm,
    editingInventory, setEditingInventory,
    inventoryToDelete,
    selectedInventoryGroup,
    groupToDelete,
    deleteConfirmText, setDeleteConfirmText,
    addProductExpanded, setAddProductExpanded,
    pdfFormSiteId, setPdfFormSiteId,
    pdfFormPeriod, setPdfFormPeriod,
    // Data
    sites, products, sortedProducts, supplierProducts, periodOptions,
    receivedProductIds = null,
    // Handlers
    handleAddInventoryItem, handleRemoveInventoryItem, handleSaveInventory, resetInventoryForm,
    handleAddProductFromDetail, handleSaveReception,
    handleDeleteMovement, handleConfirmDeleteInventory,
    handleFirstConfirmDeleteGroup, handleConfirmDeleteGroup,
    handleDownloadInventoryFormPdf,
    handleEditInventory, handleDeleteInventoryClick,
    // Utilities
    getSupplierProductsForProduct, calculateZoneTotal, calculateLineTotal,
    renderInventoryRows, renderInventoryCards, getInvItemData,
    canEdit,
    // Templates (fiches d'inventaire)
    inventoryFormTemplates = [], onApplyTemplate, onApplyTemplateFromDetail,
}) {
    // Auto-expand "Ajouter un produit" when the inventory is empty (new flow).
    useEffect(() => {
        if (inventoryDetailDialog && selectedInventoryGroup) {
            const isEmpty = !selectedInventoryGroup.items || selectedInventoryGroup.items.length === 0;
            if (isEmpty && !addProductExpanded) {
                setAddProductExpanded(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventoryDetailDialog, selectedInventoryGroup?.items?.length]);
    return (
        <>
            {/* Inventory Dialog - Multi-product form */}
            <Dialog open={inventoryDialog} onOpenChange={(open) => { setInventoryDialog(open); if (!open) { setEditingInventory(null); resetInventoryForm(); } }}>
                <DialogContent className="sm:max-w-6xl sm:max-h-[90vh] overflow-auto flex flex-col p-3 sm:p-6">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="text-sm sm:text-base">{editingInventory ? 'Modifier l\'inventaire' : 'Saisie d\'inventaire'}</DialogTitle>
                    </DialogHeader>
                    
                    {/* Fixed Header - Site, Période, Type */}
                    <div className="flex-shrink-0 grid grid-cols-3 gap-1.5 sm:gap-4 py-2 border-b">
                        <div className="space-y-0.5">
                            <Label className="text-[10px] sm:text-xs">Site *</Label>
                            <Select
                                value={inventoryForm.site_id}
                                onValueChange={(value) => setInventoryForm({ ...inventoryForm, site_id: value })}
                                disabled={editingInventory !== null}
                            >
                                <SelectTrigger className="h-8 text-xs" data-testid="inventory-site-select">
                                    <SelectValue placeholder="Site" />
                                </SelectTrigger>
                                <SelectContent>
                                    {sites.map(s => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-0.5">
                            <Label className="text-[10px] sm:text-xs">Période *</Label>
                            <Select
                                value={inventoryForm.period}
                                onValueChange={(value) => setInventoryForm({ ...inventoryForm, period: value })}
                                disabled={editingInventory !== null}
                            >
                                <SelectTrigger className="h-8 text-xs" data-testid="inventory-period-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {periodOptions.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-0.5">
                            <Label className="text-[10px] sm:text-xs">Type *</Label>
                            <Select
                                value={inventoryForm.inventory_type}
                                onValueChange={(value) => setInventoryForm({ ...inventoryForm, inventory_type: value })}
                                disabled={editingInventory !== null}
                            >
                                <SelectTrigger className="h-8 text-xs" data-testid="inventory-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="initial">Stock Initial</SelectItem>
                                    <SelectItem value="intermediate">Intermédiaire</SelectItem>
                                    <SelectItem value="final">Final</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Template selector (only when creating a new inventory) */}
                    {!editingInventory && inventoryFormTemplates.length > 0 && (
                        <div className="flex-shrink-0 py-2 border-b bg-amber-50/50">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <Label className="text-[11px] sm:text-xs text-amber-800 whitespace-nowrap flex items-center gap-1">
                                    <FileText className="w-3.5 h-3.5" />
                                    Initialiser depuis une fiche :
                                </Label>
                                <Select
                                    value=""
                                    onValueChange={(templateId) => {
                                        if (templateId && onApplyTemplate) onApplyTemplate(templateId);
                                    }}
                                >
                                    <SelectTrigger className="h-8 text-xs bg-white border-amber-300 hover:border-amber-500" data-testid="inventory-template-select">
                                        <SelectValue placeholder={`Choisir un modèle (${inventoryFormTemplates.length} disponible${inventoryFormTemplates.length > 1 ? 's' : ''})...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {inventoryFormTemplates.map(t => (
                                            <SelectItem key={t.id} value={t.id}>
                                                {t.name}
                                                {t.items?.length ? ` · ${t.items.length} produit${t.items.length > 1 ? 's' : ''}` : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    {/* Fixed - Add products section */}
                    {!editingInventory && (
                        <div className="flex-shrink-0 border-b py-2">
                            <div className="grid grid-cols-2 gap-1.5 items-end">
                                <div className="space-y-0.5">
                                    <Label className="text-[10px]">Produit</Label>
                                    <Select
                                        value={inventoryItem.industrial_product_id}
                                        onValueChange={(value) => setInventoryItem({ ...inventoryItem, industrial_product_id: value, supplier_product_id: '' })}
                                    >
                                        <SelectTrigger className="h-8 text-xs" data-testid="inventory-item-product-select">
                                            <SelectValue placeholder="Produit" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(() => {
                                                let currentCategory = null;
                                                return sortedProducts.map(p => {
                                                    const category = p.category || 'Sans catégorie';
                                                    const showHeader = category !== currentCategory;
                                                    currentCategory = category;
                                                    return (
                                                        <React.Fragment key={p.id}>
                                                            {showHeader && (
                                                                <div className="px-2 py-0 text-[10px] font-semibold text-slate-500 bg-slate-100">{category}</div>
                                                            )}
                                                            <SelectItem value={p.id} className="py-0.5 text-xs">{p.name}</SelectItem>
                                                        </React.Fragment>
                                                    );
                                                });
                                            })()}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-0.5">
                                    <Label className="text-[10px]">Fournisseur (opt.)</Label>
                                    <Select
                                        value={inventoryItem.supplier_product_id || '__none__'}
                                        onValueChange={(value) => setInventoryItem({ ...inventoryItem, supplier_product_id: value === '__none__' ? '' : value })}
                                        disabled={!inventoryItem.industrial_product_id}
                                    >
                                        <SelectTrigger className="h-8 text-xs" data-testid="inventory-item-supplier-product-select">
                                            <SelectValue placeholder="Optionnel" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">Aucun</SelectItem>
                                            {getSupplierProductsForProduct(inventoryItem.industrial_product_id).map(sp => (
                                                <SelectItem key={sp.id} value={sp.id} className="py-0.5 text-xs">
                                                    {sp.supplier_name_product || sp.supplier_ref}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-6 gap-1.5 items-end mt-1.5">
                                <div className="space-y-0.5">
                                    <Label className="text-[10px]">Unité</Label>
                                    <Select value={inventoryItem.input_unit} onValueChange={(value) => setInventoryItem({ ...inventoryItem, input_unit: value })}>
                                        <SelectTrigger className="h-8 text-xs" data-testid="inventory-item-unit-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {(() => {
                                                    const sp = supplierProducts.find(s => s.id === inventoryItem.supplier_product_id);
                                                    const product = products.find(p => p.id === inventoryItem.industrial_product_id);
                                                    const { purchaseUnitLabel, baseUnit } = resolveInventoryUnits(sp, product);
                                                    return (
                                                        <>
                                                            <SelectItem value="base">{baseUnit}</SelectItem>
                                                            <SelectItem value="purchase">{purchaseUnitLabel}</SelectItem>
                                                        </>
                                                    );
                                                })()}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-0.5">
                                        <Label className="text-[10px]">Rés.</Label>
                                        <Input type="number" step={qtyStepFor(supplierProducts.find(s => s.id === inventoryItem.supplier_product_id))} className="h-8 text-xs" value={inventoryItem.quantity_reserve} onChange={(e) => setInventoryItem({ ...inventoryItem, quantity_reserve: e.target.value })} placeholder="0" data-testid="inventory-item-reserve-input" />
                                </div>
                                <div className="space-y-0.5">
                                    <Label className="text-[10px]">Std.</Label>
                                    <Input type="number" step={qtyStepFor(supplierProducts.find(s => s.id === inventoryItem.supplier_product_id))} className="h-8 text-xs" value={inventoryItem.quantity_stand} onChange={(e) => setInventoryItem({ ...inventoryItem, quantity_stand: e.target.value })} placeholder="0" data-testid="inventory-item-stand-input" />
                                </div>
                                <div className="space-y-0.5">
                                    <Label className="text-[10px]">Siè.</Label>
                                    <Input type="number" step={qtyStepFor(supplierProducts.find(s => s.id === inventoryItem.supplier_product_id))} className="h-8 text-xs" value={inventoryItem.quantity_siege} onChange={(e) => setInventoryItem({ ...inventoryItem, quantity_siege: e.target.value })} placeholder="0" data-testid="inventory-item-siege-input" />
                                </div>
                                <div className="space-y-0.5">
                                    <Label className="text-[10px]">Total</Label>
                                    <div className="h-8 flex items-center justify-center bg-slate-100 rounded-md text-xs font-semibold text-emerald-600">
                                        {calculateZoneTotal(inventoryItem.quantity_reserve, inventoryItem.quantity_stand, inventoryItem.quantity_siege).toFixed(2)}
                                    </div>
                                </div>
                                <div className="flex items-end">
                                    <Button type="button" onClick={handleAddInventoryItem} className="w-full h-8 bg-emerald-500 hover:bg-emerald-600" data-testid="add-inventory-item-btn">
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Scrollable Items list */}
                    <div className="flex-1 overflow-auto py-2 sm:py-4 min-h-0">
                        {inventoryForm.items.length > 0 && (
                            <>
                            {/* Desktop table view */}
                            <div className="hidden sm:block border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-100 sticky top-0 z-10">
                                        <tr>
                                            <th className="text-left p-3">Produit</th>
                                            <th className="text-left p-3">Fournisseur</th>
                                            <th className="text-center p-3">Unité</th>
                                            <th className="text-right p-3">Réserve</th>
                                            <th className="text-right p-3">Stand</th>
                                            <th className="text-right p-3">Siège</th>
                                            <th className="text-right p-3">Total</th>
                                            <th className="text-right p-3">Valeur</th>
                                            {!editingInventory && <th className="p-3 w-10"></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inventoryForm.items.map((item, index) => (
                                            <tr key={index} className="border-t">
                                                <td className="p-3 font-medium">{item.product_name}</td>
                                                <td className="p-3 text-slate-600 text-xs">
                                                    {item.supplier_product_id ? (() => {
                                                        const sp = (supplierProducts || []).find(s => s.id === item.supplier_product_id);
                                                        const supplierCompany = sp?.supplier_name || '';
                                                        return (
                                                            <div className="flex items-center gap-2">
                                                                <div className="flex-1 min-w-0 leading-tight">
                                                                    {supplierCompany && (
                                                                        <span className="inline-block px-1.5 py-0.5 mr-1 rounded bg-slate-100 text-slate-600 text-[10px] font-medium uppercase tracking-wide align-middle">
                                                                            {supplierCompany}
                                                                        </span>
                                                                    )}
                                                                    <span className="truncate align-middle">{item.supplier_product_name || '-'}</span>
                                                                </div>
                                                                {editingInventory && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-6 w-6 p-0 text-slate-400 hover:text-slate-700"
                                                                        onClick={() => {
                                                                            const newItems = [...inventoryForm.items];
                                                                            newItems[index].supplier_product_id = '';
                                                                            newItems[index].supplier_product_name = '';
                                                                            setInventoryForm({ ...inventoryForm, items: newItems });
                                                                        }}
                                                                        title="Retirer le fournisseur"
                                                                        data-testid={`inventory-item-clear-supplier-${index}`}
                                                                    >
                                                                        <X className="w-3 h-3" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        );
                                                    })() : (
                                                        <Select
                                                            value=""
                                                            onValueChange={(value) => {
                                                                const sp = (supplierProducts || []).find(s => s.id === value);
                                                                if (!sp) return;
                                                                const newItems = [...inventoryForm.items];
                                                                newItems[index].supplier_product_id = sp.id;
                                                                newItems[index].supplier_product_name = sp.supplier_name_product || sp.supplier_ref;
                                                                if (sp.unit_price != null) newItems[index].unit_price = sp.unit_price;
                                                                setInventoryForm({ ...inventoryForm, items: newItems });
                                                            }}
                                                        >
                                                            <SelectTrigger className="h-7 text-xs border-dashed border-orange-300 hover:border-orange-500 text-orange-600" data-testid={`inventory-item-add-supplier-${index}`}>
                                                                <SelectValue placeholder="+ Ajouter un fournisseur" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {(supplierProducts || [])
                                                                    .filter(sp => sp.industrial_product_id === item.industrial_product_id)
                                                                    .map(sp => (
                                                                        <SelectItem key={sp.id} value={sp.id}>
                                                                            {sp.supplier_name ? `${sp.supplier_name} - ` : ''}{sp.supplier_name_product || sp.supplier_ref}
                                                                        </SelectItem>
                                                                    ))}
                                                                {(supplierProducts || []).filter(sp => sp.industrial_product_id === item.industrial_product_id).length === 0 && (
                                                                    <div className="px-2 py-1.5 text-xs text-slate-400">Aucun fournisseur associé à ce produit</div>
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                </td>
                                                <td className="p-3 text-center">
                                                    <Select
                                                        value={item.input_unit || 'base'}
                                                        onValueChange={(value) => {
                                                            const newItems = [...inventoryForm.items];
                                                            newItems[index].input_unit = value;
                                                            setInventoryForm({ ...inventoryForm, items: newItems });
                                                        }}
                                                    >
                                                        <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="base">{item.product_unit || 'pièce'}</SelectItem>
                                                            <SelectItem value="purchase">{item.purchase_unit || 'Unité achat'}</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </td>
                                                <td className="p-3 text-right">
                                                    <Input type="number" step={qtyStepFor(supplierProducts.find(s => s.id === item.supplier_product_id))} value={item.quantity_reserve || ''} onChange={(e) => {
                                                        const newItems = [...inventoryForm.items];
                                                        newItems[index].quantity_reserve = parseFloat(e.target.value) || 0;
                                                        newItems[index].quantity = (newItems[index].quantity_reserve || 0) + (newItems[index].quantity_stand || 0) + (newItems[index].quantity_siege || 0);
                                                        setInventoryForm({ ...inventoryForm, items: newItems });
                                                    }} className="w-20 text-right" />
                                                </td>
                                                <td className="p-3 text-right">
                                                    <Input type="number" step={qtyStepFor(supplierProducts.find(s => s.id === item.supplier_product_id))} value={item.quantity_stand || ''} onChange={(e) => {
                                                        const newItems = [...inventoryForm.items];
                                                        newItems[index].quantity_stand = parseFloat(e.target.value) || 0;
                                                        newItems[index].quantity = (newItems[index].quantity_reserve || 0) + (newItems[index].quantity_stand || 0) + (newItems[index].quantity_siege || 0);
                                                        setInventoryForm({ ...inventoryForm, items: newItems });
                                                    }} className="w-20 text-right" />
                                                </td>
                                                <td className="p-3 text-right">
                                                    <Input type="number" step={qtyStepFor(supplierProducts.find(s => s.id === item.supplier_product_id))} value={item.quantity_siege || ''} onChange={(e) => {
                                                        const newItems = [...inventoryForm.items];
                                                        newItems[index].quantity_siege = parseFloat(e.target.value) || 0;
                                                        newItems[index].quantity = (newItems[index].quantity_reserve || 0) + (newItems[index].quantity_stand || 0) + (newItems[index].quantity_siege || 0);
                                                        setInventoryForm({ ...inventoryForm, items: newItems });
                                                    }} className="w-20 text-right" />
                                                </td>
                                                <td className="p-3 text-right font-semibold bg-slate-50">{item.quantity?.toFixed(2) || '0.00'}</td>
                                                <td className="p-3 text-right font-semibold text-emerald-600">{formatCurrency(calculateLineTotal(item))}</td>
                                                {!editingInventory && (
                                                    <td className="p-3 text-center">
                                                        <Button variant="ghost" size="sm" onClick={() => handleRemoveInventoryItem(index)} className="text-red-500 hover:text-red-700">
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile card view */}
                            <div className="sm:hidden space-y-2">
                                {inventoryForm.items.map((item, index) => (
                                    <div key={index} className="border rounded-lg p-2 bg-white">
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-sm text-slate-800 break-words">{item.product_name}</div>
                                                {item.supplier_product_name ? (() => {
                                                    const sp = (supplierProducts || []).find(s => s.id === item.supplier_product_id);
                                                    const supplierCompany = sp?.supplier_name || '';
                                                    return (
                                                        <div className="text-[11px] text-slate-500 break-words flex items-center gap-1 mt-0.5">
                                                            <span className="flex-1 leading-tight">
                                                                {supplierCompany && (
                                                                    <span className="inline-block px-1.5 py-0.5 mr-1 rounded bg-slate-100 text-slate-600 text-[10px] font-medium uppercase tracking-wide align-middle">
                                                                        {supplierCompany}
                                                                    </span>
                                                                )}
                                                                <span className="align-middle">{item.supplier_product_name}</span>
                                                            </span>
                                                            {editingInventory && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-5 w-5 p-0 text-slate-400"
                                                                    onClick={() => {
                                                                        const newItems = [...inventoryForm.items];
                                                                        newItems[index].supplier_product_id = '';
                                                                        newItems[index].supplier_product_name = '';
                                                                        setInventoryForm({ ...inventoryForm, items: newItems });
                                                                    }}
                                                                    data-testid={`mobile-clear-supplier-${index}`}
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    );
                                                })() : editingInventory ? (
                                                    <Select
                                                        value=""
                                                        onValueChange={(value) => {
                                                            const sp = (supplierProducts || []).find(s => s.id === value);
                                                            if (!sp) return;
                                                            const newItems = [...inventoryForm.items];
                                                            newItems[index].supplier_product_id = sp.id;
                                                            newItems[index].supplier_product_name = sp.supplier_name_product || sp.supplier_ref;
                                                            if (sp.unit_price != null) newItems[index].unit_price = sp.unit_price;
                                                            setInventoryForm({ ...inventoryForm, items: newItems });
                                                        }}
                                                    >
                                                        <SelectTrigger className="h-6 text-[11px] mt-1 border-dashed border-orange-300 text-orange-600" data-testid={`mobile-add-supplier-${index}`}>
                                                            <SelectValue placeholder="+ Ajouter un fournisseur" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {(supplierProducts || [])
                                                                .filter(sp => sp.industrial_product_id === item.industrial_product_id)
                                                                .map(sp => (
                                                                    <SelectItem key={sp.id} value={sp.id}>
                                                                        {sp.supplier_name ? `${sp.supplier_name} - ` : ''}{sp.supplier_name_product || sp.supplier_ref}
                                                                    </SelectItem>
                                                                ))}
                                                        </SelectContent>
                                                    </Select>
                                                ) : null}
                                            </div>
                                            {!editingInventory && (
                                                <Button variant="ghost" size="icon" onClick={() => handleRemoveInventoryItem(index)} className="h-7 w-7 text-red-500 hover:text-red-700 flex-shrink-0">
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 mb-2">
                                            <div className="space-y-0.5">
                                                <Label className="text-[10px] text-slate-500">Unité</Label>
                                                <Select
                                                    value={item.input_unit || 'base'}
                                                    onValueChange={(value) => {
                                                        const newItems = [...inventoryForm.items];
                                                        newItems[index].input_unit = value;
                                                        setInventoryForm({ ...inventoryForm, items: newItems });
                                                    }}
                                                >
                                                    <SelectTrigger className="h-8 text-xs" data-testid={`mobile-edit-unit-${index}`}><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="base">{item.product_unit || 'pièce'}</SelectItem>
                                                        <SelectItem value="purchase">{item.purchase_unit || 'Unité achat'}</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-0.5">
                                                <Label className="text-[10px] text-slate-500">Total</Label>
                                                <div className="h-8 flex items-center justify-center bg-slate-100 rounded-md text-xs font-semibold text-slate-700">
                                                    {item.quantity?.toFixed(2) || '0.00'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-1.5">
                                            <div className="space-y-0.5">
                                                <Label className="text-[10px] text-slate-500">Réserve</Label>
                                                <Input type="number" inputMode="decimal" step={qtyStepFor(supplierProducts.find(s => s.id === item.supplier_product_id))} value={item.quantity_reserve || ''} onChange={(e) => {
                                                    const newItems = [...inventoryForm.items];
                                                    newItems[index].quantity_reserve = parseFloat(e.target.value) || 0;
                                                    newItems[index].quantity = (newItems[index].quantity_reserve || 0) + (newItems[index].quantity_stand || 0) + (newItems[index].quantity_siege || 0);
                                                    setInventoryForm({ ...inventoryForm, items: newItems });
                                                }} className="h-9 text-sm text-right" data-testid={`mobile-edit-reserve-${index}`} />
                                            </div>
                                            <div className="space-y-0.5">
                                                <Label className="text-[10px] text-slate-500">Stand</Label>
                                                <Input type="number" inputMode="decimal" step={qtyStepFor(supplierProducts.find(s => s.id === item.supplier_product_id))} value={item.quantity_stand || ''} onChange={(e) => {
                                                    const newItems = [...inventoryForm.items];
                                                    newItems[index].quantity_stand = parseFloat(e.target.value) || 0;
                                                    newItems[index].quantity = (newItems[index].quantity_reserve || 0) + (newItems[index].quantity_stand || 0) + (newItems[index].quantity_siege || 0);
                                                    setInventoryForm({ ...inventoryForm, items: newItems });
                                                }} className="h-9 text-sm text-right" data-testid={`mobile-edit-stand-${index}`} />
                                            </div>
                                            <div className="space-y-0.5">
                                                <Label className="text-[10px] text-slate-500">Siège</Label>
                                                <Input type="number" inputMode="decimal" step={qtyStepFor(supplierProducts.find(s => s.id === item.supplier_product_id))} value={item.quantity_siege || ''} onChange={(e) => {
                                                    const newItems = [...inventoryForm.items];
                                                    newItems[index].quantity_siege = parseFloat(e.target.value) || 0;
                                                    newItems[index].quantity = (newItems[index].quantity_reserve || 0) + (newItems[index].quantity_stand || 0) + (newItems[index].quantity_siege || 0);
                                                    setInventoryForm({ ...inventoryForm, items: newItems });
                                                }} className="h-9 text-sm text-right" data-testid={`mobile-edit-siege-${index}`} />
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between mt-2 pt-2 border-t text-xs">
                                            <span className="text-slate-500">Valeur</span>
                                            <span className="font-semibold text-emerald-600">{formatCurrency(calculateLineTotal(item))}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            </>
                        )}
                        {inventoryForm.items.length === 0 && !editingInventory && (
                            <p className="text-center text-slate-500 py-8">Aucun produit ajouté</p>
                        )}
                    </div>
                    
                    {/* Fixed Footer */}
                    <div className="flex-shrink-0 border-t pt-4 space-y-4">
                        {inventoryForm.items.length > 0 && (
                            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                                <span className="font-semibold text-emerald-700">
                                    Total inventaire ({inventoryForm.items.length} produit{inventoryForm.items.length > 1 ? 's' : ''}) :
                                </span>
                                <span className="text-xl font-bold text-emerald-600">
                                    {formatCurrency(inventoryForm.items.reduce((sum, item) => sum + calculateLineTotal(item), 0))}
                                </span>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setInventoryDialog(false); setEditingInventory(null); resetInventoryForm(); }}>Annuler</Button>
                            <Button onClick={handleSaveInventory} className="btn-primary" data-testid="save-inventory-btn" disabled={inventoryForm.items.length === 0}>
                                {editingInventory ? 'Modifier' : `Enregistrer (${inventoryForm.items.length} produit${inventoryForm.items.length > 1 ? 's' : ''})`}
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Inventory Detail Dialog */}
            <Dialog open={inventoryDetailDialog} onOpenChange={(open) => {
                setInventoryDetailDialog(open);
                if (!open) setDetailAddItem({ industrial_product_id: '', supplier_product_id: '', quantity: '' });
            }}>
                <DialogContent className="!max-w-[95vw] sm:!max-w-6xl w-[95vw] sm:w-full sm:h-[90vh] sm:max-h-[90vh] overflow-hidden flex flex-col p-2 sm:p-3">
                    <DialogHeader className="flex-shrink-0 flex flex-row items-center justify-between gap-2 pb-1">
                        <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
                            <DialogTitle className="text-sm sm:text-base truncate">
                                {selectedInventoryGroup?.site_name} — {selectedInventoryGroup?.period}
                            </DialogTitle>
                            <DialogDescription className="text-[11px] text-slate-500 m-0">
                                {getInventoryTypeLabel(selectedInventoryGroup?.inventory_type)} · {selectedInventoryGroup?.items?.length || 0} produits
                            </DialogDescription>
                        </div>
                        {selectedInventoryGroup && (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                <InventoryAuditButton group={selectedInventoryGroup} />
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                                    downloadAuthFile(exportAPI.inventoryDetail(selectedInventoryGroup.site_id, selectedInventoryGroup.period, selectedInventoryGroup.inventory_type), 'inventaire_detail.xlsx');
                                }} data-testid="export-inventory-detail-btn">
                                    <Download className="w-3.5 h-3.5 mr-1" /> Excel
                                </Button>
                            </div>
                        )}
                    </DialogHeader>
                    
                    {/* Template selector - only when the inventory is empty */}
                    {selectedInventoryGroup && (!selectedInventoryGroup.items || selectedInventoryGroup.items.length === 0) && inventoryFormTemplates.length > 0 && onApplyTemplateFromDetail && (
                        <div className="flex-shrink-0 border-b bg-amber-50/60 px-3 py-2">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <Label className="text-[11px] sm:text-xs text-amber-800 whitespace-nowrap flex items-center gap-1">
                                    <FileText className="w-3.5 h-3.5" />
                                    Initialiser depuis une fiche :
                                </Label>
                                <Select
                                    value=""
                                    onValueChange={(templateId) => {
                                        if (templateId) onApplyTemplateFromDetail(templateId);
                                    }}
                                >
                                    <SelectTrigger className="h-8 text-xs bg-white border-amber-300 hover:border-amber-500" data-testid="inventory-detail-template-select">
                                        <SelectValue placeholder={`Choisir un modèle (${inventoryFormTemplates.length} disponible${inventoryFormTemplates.length > 1 ? 's' : ''})...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {inventoryFormTemplates.map(t => (
                                            <SelectItem key={t.id} value={t.id}>
                                                {t.name}
                                                {t.items?.length ? ` · ${t.items.length} produit${t.items.length > 1 ? 's' : ''}` : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    {/* Collapsible - Add product */}
                    {selectedInventoryGroup && (
                        <div className="flex-shrink-0 border-y bg-slate-50">
                            <button type="button" className="w-full flex items-center justify-between px-3 py-1 text-left" onClick={() => setAddProductExpanded(!addProductExpanded)} data-testid="toggle-add-product">
                                <Label className="text-xs font-semibold cursor-pointer">Ajouter un produit</Label>
                                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${addProductExpanded ? 'rotate-180' : ''}`} />
                            </button>
                            {addProductExpanded && (
                            <div className="px-2 pb-2">
                            <div className="flex flex-wrap items-end gap-1.5">
                                <div className="flex-1 min-w-[180px] space-y-0.5">
                                    <Label className="text-[10px] text-slate-500">Produit</Label>
                                    <Select value={detailAddItem.industrial_product_id} onValueChange={(value) => setDetailAddItem({ ...detailAddItem, industrial_product_id: value, supplier_product_id: '' })}>
                                        <SelectTrigger className="h-7 text-xs" data-testid="detail-add-product-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                        <SelectContent>
                                            {(() => {
                                                // Only keep products that have already been received on Fuji Express
                                                // sites (manual_receptions or delivered orders). When the list isn't
                                                // loaded yet (null), fall back to all products to avoid an empty UI.
                                                const allowedSet = receivedProductIds && receivedProductIds.length
                                                    ? new Set(receivedProductIds)
                                                    : null;
                                                const list = allowedSet
                                                    ? sortedProducts.filter(p => allowedSet.has(p.id))
                                                    : sortedProducts;
                                                if (list.length === 0) {
                                                    return (
                                                        <div className="px-2 py-2 text-xs text-slate-400 italic">
                                                            Aucun produit reçu sur Fuji Express.
                                                        </div>
                                                    );
                                                }
                                                let currentCategory = null;
                                                return list.map(p => {
                                                    const category = p.category || 'Sans catégorie';
                                                    const showHeader = category !== currentCategory;
                                                    currentCategory = category;
                                                    return (
                                                        <React.Fragment key={p.id}>
                                                            {showHeader && (
                                                                <div className="px-2 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 bg-slate-100 border-t border-slate-200 first:border-t-0">
                                                                    {category}
                                                                </div>
                                                            )}
                                                            <SelectItem value={p.id} className="pl-6">
                                                                {p.name}
                                                            </SelectItem>
                                                        </React.Fragment>
                                                    );
                                                });
                                            })()}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex-1 min-w-[160px] space-y-0.5">
                                    <Label className="text-[10px] text-slate-500">Fournisseur (opt.)</Label>
                                    <Select value={detailAddItem.supplier_product_id || '__none__'} onValueChange={(value) => setDetailAddItem({ ...detailAddItem, supplier_product_id: value === '__none__' ? '' : value })} disabled={!detailAddItem.industrial_product_id}>
                                        <SelectTrigger className="h-7 text-xs" data-testid="detail-add-supplier-product-select"><SelectValue placeholder="Optionnel" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">Aucun</SelectItem>
                                            {getSupplierProductsForProduct(detailAddItem.industrial_product_id).map(sp => (
                                                <SelectItem key={sp.id} value={sp.id}>{sp.supplier_name_product || sp.supplier_ref} ({sp.supplier_name})</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="w-20 space-y-0.5">
                                    <Label className="text-[10px] text-slate-500">Unité</Label>
                                    <Select value={detailAddItem.input_unit} onValueChange={(value) => setDetailAddItem({ ...detailAddItem, input_unit: value })}>
                                        <SelectTrigger className="h-7 text-xs px-2" data-testid="detail-add-unit-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {(() => {
                                                const sp = supplierProducts.find(s => s.id === detailAddItem.supplier_product_id);
                                                const product = products.find(p => p.id === detailAddItem.industrial_product_id);
                                                const { purchaseUnitLabel, baseUnit } = resolveInventoryUnits(sp, product);
                                                return (
                                                    <>
                                                        <SelectItem value="base">{baseUnit}</SelectItem>
                                                        <SelectItem value="purchase">{purchaseUnitLabel}</SelectItem>
                                                    </>
                                                );
                                            })()}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="w-14 space-y-0.5">
                                    <Label className="text-[10px] text-slate-500">Rés.</Label>
                                    <Input type="number" step={qtyStepFor(supplierProducts.find(s => s.id === detailAddItem.supplier_product_id))} className="h-7 text-xs px-1.5 text-center" value={detailAddItem.quantity_reserve} onChange={(e) => setDetailAddItem({ ...detailAddItem, quantity_reserve: e.target.value })} placeholder="0" data-testid="detail-add-reserve-input" />
                                </div>
                                <div className="w-14 space-y-0.5">
                                    <Label className="text-[10px] text-slate-500">Std.</Label>
                                    <Input type="number" step={qtyStepFor(supplierProducts.find(s => s.id === detailAddItem.supplier_product_id))} className="h-7 text-xs px-1.5 text-center" value={detailAddItem.quantity_stand} onChange={(e) => setDetailAddItem({ ...detailAddItem, quantity_stand: e.target.value })} placeholder="0" data-testid="detail-add-stand-input" />
                                </div>
                                <div className="w-14 space-y-0.5">
                                    <Label className="text-[10px] text-slate-500">Siè.</Label>
                                    <Input type="number" step={qtyStepFor(supplierProducts.find(s => s.id === detailAddItem.supplier_product_id))} className="h-7 text-xs px-1.5 text-center" value={detailAddItem.quantity_siege} onChange={(e) => setDetailAddItem({ ...detailAddItem, quantity_siege: e.target.value })} placeholder="0" data-testid="detail-add-siege-input" />
                                </div>
                                <div className="w-16 space-y-0.5">
                                    <Label className="text-[10px] text-slate-500">Total</Label>
                                    <div className="h-7 flex items-center justify-center bg-slate-100 rounded-md text-xs font-semibold text-emerald-600">
                                        {calculateZoneTotal(detailAddItem.quantity_reserve, detailAddItem.quantity_stand, detailAddItem.quantity_siege).toFixed(2)}
                                    </div>
                                </div>
                                <Button type="button" onClick={handleAddProductFromDetail} className="h-7 px-3 bg-emerald-500 hover:bg-emerald-600" data-testid="detail-add-product-btn">
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                            </div>
                            )}
                        </div>
                    )}
                    
                    {/* Scrollable Products */}
                    <div className="flex-1 min-h-0 overflow-auto">
                        {selectedInventoryGroup && (
                            <>
                            <div className="hidden sm:block border rounded-lg overflow-auto max-h-full">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-100 sticky top-0 z-10">
                                        <tr>
                                            <th className="text-left p-3 whitespace-nowrap">Produit</th>
                                            <th className="text-left p-3 whitespace-nowrap">Fournisseur</th>
                                            <th className="text-right p-3 whitespace-nowrap">Réserve</th>
                                            <th className="text-right p-3 whitespace-nowrap">Stand</th>
                                            <th className="text-right p-3 whitespace-nowrap">Siège</th>
                                            <th className="text-right p-3 whitespace-nowrap">Total</th>
                                            <th className="text-right p-3 whitespace-nowrap">Valeur</th>
                                            <th className="text-center p-3 whitespace-nowrap">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>{renderInventoryRows()}</tbody>
                                </table>
                            </div>
                            <div className="sm:hidden space-y-1">{renderInventoryCards()}</div>
                            </>
                        )}
                    </div>
                    
                    {/* Compact footer: total + close on one row */}
                    <div className="flex-shrink-0 border-t pt-1.5 flex items-center justify-between gap-3 px-3">
                        {selectedInventoryGroup ? (
                            <div className="flex items-baseline gap-2">
                                <span className="text-xs font-semibold text-emerald-700">
                                    Total ({selectedInventoryGroup.items.length} produit{selectedInventoryGroup.items.length > 1 ? 's' : ''}) :
                                </span>
                                <span className="text-base font-bold text-emerald-600 tabular-nums" data-testid="inventory-detail-total">
                                    {formatCurrency(
                                        selectedInventoryGroup.items.reduce((sum, inv) => {
                                            const { lineTotal } = getInvItemData(inv);
                                            return sum + lineTotal;
                                        }, 0)
                                    )}
                                </span>
                            </div>
                        ) : <div />}
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setInventoryDetailDialog(false)}>Fermer</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Reception Dialog */}
            <Dialog open={receptionDialog} onOpenChange={setReceptionDialog}>
                <DialogContent className="sm:max-w-lg p-3 sm:p-6">
                    <DialogHeader><DialogTitle>Saisie de réception</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Site *</Label>
                                <Select value={receptionForm.site_id} onValueChange={(value) => setReceptionForm({ ...receptionForm, site_id: value })}>
                                    <SelectTrigger data-testid="reception-site-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                    <SelectContent>{sites.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Date *</Label>
                                <Input type="date" value={receptionForm.date} onChange={(e) => setReceptionForm({ ...receptionForm, date: e.target.value })} data-testid="reception-date-input" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Produit industriel *</Label>
                            <Select value={receptionForm.industrial_product_id} onValueChange={(value) => setReceptionForm({ ...receptionForm, industrial_product_id: value, supplier_product_id: '' })}>
                                <SelectTrigger data-testid="reception-product-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                <SelectContent>{products.map(p => (<SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>))}</SelectContent>
                            </Select>
                        </div>
                        {receptionForm.industrial_product_id && getSupplierProductsForProduct(receptionForm.industrial_product_id).length > 0 && (
                            <div className="space-y-2">
                                <Label>Produit fournisseur (optionnel)</Label>
                                <Select value={receptionForm.supplier_product_id} onValueChange={(value) => setReceptionForm({ ...receptionForm, supplier_product_id: value })}>
                                    <SelectTrigger data-testid="reception-supplier-product-select"><SelectValue placeholder="Sélectionner (optionnel)" /></SelectTrigger>
                                    <SelectContent>
                                        {getSupplierProductsForProduct(receptionForm.industrial_product_id).map(sp => (
                                            <SelectItem key={sp.id} value={sp.id}>{sp.supplier_name_product || sp.supplier_ref} ({sp.supplier_name})</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label>Quantité réceptionnée *</Label>
                            <Input type="number" step="0.01" value={receptionForm.quantity} onChange={(e) => setReceptionForm({ ...receptionForm, quantity: e.target.value })} placeholder="0" data-testid="reception-quantity-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Input value={receptionForm.notes} onChange={(e) => setReceptionForm({ ...receptionForm, notes: e.target.value })} placeholder="Notes optionnelles" data-testid="reception-notes-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReceptionDialog(false)}>Annuler</Button>
                        <Button onClick={handleSaveReception} className="btn-primary" data-testid="save-reception-btn">Enregistrer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Movement */}
            <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>Êtes-vous sûr de vouloir supprimer ce mouvement de stock ?</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteMovement} className="bg-red-500 hover:bg-red-600">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Inventory */}
            <AlertDialog open={deleteInventoryDialog} onOpenChange={setDeleteInventoryDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer l'inventaire ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer cette ligne d'inventaire ?
                            {inventoryToDelete && (
                                <span className="block mt-2 font-medium">
                                    {inventoryToDelete.product_name} - {inventoryToDelete.quantity} unités ({getInventoryTypeLabel(inventoryToDelete.inventory_type)})
                                </span>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmDeleteInventory} className="bg-red-500 hover:bg-red-600">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Group - First confirmation */}
            <AlertDialog open={deleteGroupDialog} onOpenChange={setDeleteGroupDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-600">Supprimer l'inventaire complet ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="block mb-3">Vous êtes sur le point de supprimer <strong>l'intégralité</strong> de cet inventaire.</span>
                            {groupToDelete && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-md space-y-1">
                                    <div className="text-lg font-bold text-slate-900 border-b border-red-200 pb-2 mb-2">{groupToDelete.site_name}</div>
                                    <div><strong>Période :</strong> {groupToDelete.period}</div>
                                    <div><strong>Type :</strong> {getInventoryTypeLabel(groupToDelete.inventory_type)}</div>
                                    <div className="text-red-600 font-semibold mt-2">{groupToDelete.total_items} produit(s) seront supprimés</div>
                                </div>
                            )}
                            <span className="block mt-3 text-red-600 font-medium">Cette action est irréversible.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleFirstConfirmDeleteGroup} className="bg-red-500 hover:bg-red-600">Continuer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Group - Second confirmation */}
            <Dialog open={deleteGroupConfirmDialog} onOpenChange={(open) => { setDeleteGroupConfirmDialog(open); if (!open) setDeleteConfirmText(''); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Confirmation finale</DialogTitle>
                        <DialogDescription>
                            <span className="block mb-4">Pour confirmer la suppression de cet inventaire, tapez <strong className="text-red-600">SUPPRIMER</strong> ci-dessous :</span>
                            {groupToDelete && (
                                <div className="p-3 bg-slate-100 rounded-md text-sm mb-4">
                                    {groupToDelete.site_name} - {groupToDelete.period} - {getInventoryTypeLabel(groupToDelete.inventory_type)}<br />
                                    <span className="text-red-600 font-medium">{groupToDelete.total_items} produit(s)</span>
                                </div>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="Tapez SUPPRIMER" className="text-center font-mono text-lg" data-testid="delete-confirm-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeleteGroupConfirmDialog(false); setDeleteConfirmText(''); }}>Annuler</Button>
                        <Button variant="destructive" onClick={handleConfirmDeleteGroup} disabled={deleteConfirmText !== 'SUPPRIMER'} data-testid="confirm-delete-group-btn">
                            Supprimer définitivement
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Inventory Form PDF */}
            <Dialog open={inventoryFormPdfDialog} onOpenChange={setInventoryFormPdfDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-amber-600" /> Fiche d'inventaire PDF
                        </DialogTitle>
                        <DialogDescription>
                            Générez une fiche d'inventaire à imprimer pour un site et une période donnés. Les produits listés correspondent à la consommation du site.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Site *</Label>
                            <Select value={pdfFormSiteId} onValueChange={setPdfFormSiteId}>
                                <SelectTrigger data-testid="pdf-form-site-select"><SelectValue placeholder="Sélectionner un site" /></SelectTrigger>
                                <SelectContent>{sites.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Période</Label>
                            <Select value={pdfFormPeriod} onValueChange={setPdfFormPeriod}>
                                <SelectTrigger data-testid="pdf-form-period-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{periodOptions.map(opt => (<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>))}</SelectContent>
                            </Select>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-md text-sm text-amber-700">
                            <strong>Note :</strong> La fiche inclura uniquement les produits ayant une consommation pour le site et la période sélectionnés.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setInventoryFormPdfDialog(false)}>Annuler</Button>
                        <Button onClick={handleDownloadInventoryFormPdf} className="bg-amber-600 hover:bg-amber-700" data-testid="download-inventory-form-pdf-btn">
                            <Download className="w-4 h-4 mr-2" /> Télécharger PDF
                        </Button>
                        <PrintButton size="default" testId="print-inventory-form-pdf-btn"
                            disabled={!pdfFormSiteId}
                            url={() => sitesAPI.getInventoryFormPdfUrl(pdfFormSiteId, pdfFormPeriod)} />
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

// ============ Inventory Audit Log button + dialog ============

function InventoryAuditButton({ group }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState([]);

    const load = async () => {
        if (!group) return;
        setLoading(true);
        try {
            const res = await inventoriesAPI.getAuditLog({
                site_id: group.site_id,
                period: group.period,
                inventory_type: group.inventory_type,
                limit: 300,
            });
            setEntries(res.data?.entries || []);
        } catch (e) {
            console.error('Audit load failed', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const actionStyle = {
        create: { label: 'Création', cls: 'bg-emerald-100 text-emerald-700' },
        update: { label: 'Modification', cls: 'bg-amber-100 text-amber-700' },
        delete: { label: 'Suppression', cls: 'bg-red-100 text-red-700' },
    };

    const renderDelta = (e) => {
        const before = e.before || {};
        const after = e.after || {};
        const fields = [
            ['quantity_reserve', 'Rés.'],
            ['quantity_stand', 'Std.'],
            ['quantity_siege', 'Siè.'],
            ['quantity', 'Qté'],
            ['unit_price', 'PU'],
            ['unit_mode', 'Unité'],
        ];
        const changes = [];
        for (const [key, label] of fields) {
            const b = before?.[key];
            const a = after?.[key];
            if (e.action === 'create' && (a === undefined || a === null || a === 0 || a === '')) continue;
            if (e.action !== 'create' && b === a) continue;
            changes.push({ label, before: b, after: a });
        }
        if (changes.length === 0) return <span className="text-slate-400 italic text-xs">Aucun changement de valeur</span>;
        return (
            <div className="text-xs text-slate-600 space-y-0.5">
                {changes.map((c, i) => (
                    <div key={i}>
                        <span className="font-semibold">{c.label}</span>
                        {' : '}
                        {e.action !== 'create' && (
                            <>
                                <span className="line-through text-slate-400">{c.before ?? '-'}</span>
                                {' → '}
                            </>
                        )}
                        <span className="text-slate-800 font-semibold">{c.after ?? '-'}</span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setOpen(true)}
                data-testid="open-inventory-audit-btn"
                title="Historique des modifications"
            >
                <History className="w-3.5 h-3.5 mr-1" /> Historique
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="!max-w-[90vw] sm:!max-w-3xl w-[90vw] sm:w-full sm:max-h-[85vh] overflow-hidden flex flex-col p-3">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="text-sm sm:text-base">
                            Historique — {group?.site_name} · {group?.period} · {getInventoryTypeLabel(group?.inventory_type)}
                        </DialogTitle>
                        <DialogDescription className="text-[11px] text-slate-500">
                            Toutes les créations, modifications et suppressions sur cet inventaire.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 min-h-0 overflow-auto">
                        {loading ? (
                            <div className="text-center text-xs text-slate-400 py-6">Chargement…</div>
                        ) : entries.length === 0 ? (
                            <div className="text-center text-xs text-slate-400 py-8">
                                Aucun événement enregistré pour cet inventaire.
                                <div className="text-[10px] text-slate-300 mt-1">L'historique commence avec les nouvelles modifications.</div>
                            </div>
                        ) : (
                            <ul className="divide-y divide-slate-100" data-testid="inventory-audit-list">
                                {entries.map(e => {
                                    const a = actionStyle[e.action] || { label: e.action, cls: 'bg-slate-100 text-slate-600' };
                                    return (
                                        <li key={e.id} className="py-2 px-1 flex flex-col sm:flex-row gap-2 sm:gap-3">
                                            <div className="flex-shrink-0 sm:w-44 text-[11px] text-slate-500">
                                                {formatDate ? formatDate(e.timestamp) : new Date(e.timestamp).toLocaleString('fr-FR')}
                                                <div className="text-slate-700 font-medium">{e.user_name || '—'}</div>
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold mt-0.5 ${a.cls}`}>
                                                    {a.label}
                                                </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-semibold text-slate-700 truncate" title={e.industrial_product_name}>
                                                    {e.industrial_product_name || '(produit supprimé)'}
                                                </div>
                                                <div className="text-[11px] text-slate-500 truncate">
                                                    {e.supplier_product_name && (
                                                        <>
                                                            {e.supplier_product_name}
                                                            {e.supplier_name ? ` · ${e.supplier_name}` : ''}
                                                        </>
                                                    )}
                                                </div>
                                                <div className="mt-1">{renderDelta(e)}</div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                    <DialogFooter className="flex-shrink-0 pt-2">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOpen(false)}>Fermer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
